/**
 * Simple non-React toast notification utility
 * This implementation doesn't use any React hooks to avoid hook-related errors
 */

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  duration?: number;
  position?:
    | 'top-right'
    | 'top-center'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'bottom-left';
  icon?: string;
}

class CustomToast {
  private container: HTMLElement | null = null;

  constructor() {
    // Initialize container when needed
    this.ensureContainer();
  }

  private ensureContainer() {
    if (!document.getElementById('custom-toast-container')) {
      this.container = document.createElement('div');
      this.container.id = 'custom-toast-container';
      this.container.style.position = 'fixed';
      this.container.style.top = '90px';
      this.container.style.right = '16px';
      this.container.style.zIndex = '9000';
      this.container.style.display = 'flex';
      this.container.style.flexDirection = 'column';
      this.container.style.gap = '10px';
      document.body.appendChild(this.container);
    } else {
      this.container = document.getElementById('custom-toast-container');
    }
  }

  // Get theme-aware colors that work in both light and dark modes
  private static getColors(type: ToastType): { background: string; color: string; icon: string } {
    // Use CSS custom properties that are set by MUI theme
    const isDarkMode = document.documentElement.getAttribute('data-mui-color-scheme') === 'dark';
    const backgroundWhite = isDarkMode ? '#1e1e1e' : '#ffffff';
    
    switch (type) {
      case 'success':
        return { background: backgroundWhite, color: isDarkMode ? '#4ade80' : '#065F46', icon: '✅' };
      case 'error':
        return { background: backgroundWhite, color: isDarkMode ? '#f87171' : '#B91C1C', icon: '❌' };
      case 'warning':
        return { background: backgroundWhite, color: isDarkMode ? '#fbbf24' : '#996500', icon: '⚠️' };
      case 'info':
        return { background: backgroundWhite, color: isDarkMode ? '#60a5fa' : '#004B8D', icon: 'ℹ️' };
      default:
        return { background: backgroundWhite, color: isDarkMode ? '#60a5fa' : '#004B8D', icon: 'ℹ️' };
    }
  }

  // Changed to static method since it doesn't use 'this'
  private static createToastElement(
    message: string,
    type: ToastType,
    options: ToastOptions = {}
  ): HTMLElement {
    const { duration = 3000 } = options;
    const colors = CustomToast.getColors(type);

    // Create toast element
    const toast = document.createElement('div');
    toast.style.padding = '12px 16px';
    toast.style.borderRadius = '16px';
    toast.style.background = colors.background;
    toast.style.color = colors.color;
    // Use theme-aware shadow
    const isDarkMode = document.documentElement.getAttribute('data-mui-color-scheme') === 'dark';
    toast.style.boxShadow = isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)';
    toast.style.display = 'flex';
    toast.style.alignItems = 'center';
    toast.style.minWidth = '300px';
    toast.style.maxWidth = '400px';
    toast.style.animation = 'customToastIn 0.3s ease forwards';

    // Add icon
    const iconSpan = document.createElement('span');
    iconSpan.textContent = options.icon || colors.icon;
    iconSpan.style.marginRight = '12px';
    iconSpan.style.fontSize = '18px';

    // Add message
    const messageSpan = document.createElement('span');
    messageSpan.textContent = message;
    messageSpan.style.flex = '1';

    // Add close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.style.background = 'transparent';
    closeButton.style.border = 'none';
    closeButton.style.color = colors.color;
    closeButton.style.fontSize = '18px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.marginLeft = '12px';
    closeButton.onclick = () => CustomToast.dismissToast(toast);

    // Assemble toast
    toast.appendChild(iconSpan);
    toast.appendChild(messageSpan);
    toast.appendChild(closeButton);

    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes customToastIn {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      
      @keyframes customToastOut {
        from { opacity: 1; transform: translateX(0); }
        to { opacity: 0; transform: translateX(20px); }
      }
    `;
    document.head.appendChild(style);

    // Auto dismiss
    setTimeout(() => {
      CustomToast.dismissToast(toast);
    }, duration);

    return toast;
  }

  // Changed to static method since it doesn't use 'this'
  private static dismissToast(toast: HTMLElement) {
    toast.style.animation = 'customToastOut 0.3s ease forwards';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }

  // Public methods
  success(message: string, options: ToastOptions = {}) {
    this.ensureContainer();
    const toast = CustomToast.createToastElement(message, 'success', options);
    this.container?.appendChild(toast);
  }

  error(message: string, options: ToastOptions = {}) {
    this.ensureContainer();
    const toast = CustomToast.createToastElement(message, 'error', options);
    this.container?.appendChild(toast);
  }

  warning(message: string, options: ToastOptions = {}) {
    this.ensureContainer();
    const toast = CustomToast.createToastElement(message, 'warning', options);
    this.container?.appendChild(toast);
  }

  info(message: string, options: ToastOptions = {}) {
    this.ensureContainer();
    const toast = CustomToast.createToastElement(message, 'info', options);
    this.container?.appendChild(toast);
  }

  show(message: string, type: ToastType = 'info', options: ToastOptions = {}) {
    this.ensureContainer();
    const toast = CustomToast.createToastElement(message, type, options);
    this.container?.appendChild(toast);
  }
}

// Export singleton instance
export const customToast = new CustomToast();

// Also export the class for testing or other uses
export default CustomToast;
